import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { PARTNERS } from '@/lib/partners';
import { rememberPartner } from '@/lib/partnerBrand';

type Peek = { partner: string; newAccount: boolean; returnUrl: string | null; email?: string; firstName?: string };

// /handoff?ticket=… — where a partner's "Hey Joe" button lands (Limelite first).
// Someone who already has a Hey Joe account goes straight in. Someone new sees a
// confirm card first; their account is created only when they press Continue.
const PartnerHandoffPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [peek, setPeek] = useState<Peek | null>(null);
  const [busy, setBusy] = useState(false);
  const ticketRef = useRef<string>('');
  const ran = useRef(false);

  const redeem = useCallback(async (accept: boolean) => {
    setBusy(true);
    try {
      const res = await fetch('/api/partner/handoff/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ticket: ticketRef.current, accept }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) throw new Error(data.error || 'Could not sign you in.');
      if (data.partner) rememberPartner(data.partner);
      login(data.token, data.user);
      if (!data.created) {
        toast({ title: `Signed in as ${data.user?.email}`, description: 'Not you? Sign out from the menu at the top right.' });
      }
      navigate(data.next || '/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [login, navigate, toast]);

  useEffect(() => {
    if (ran.current) return; // StrictMode runs effects twice; the ticket is single-use
    ran.current = true;
    ticketRef.current = new URLSearchParams(window.location.search).get('ticket') || '';
    window.history.replaceState({}, '', '/handoff');
    (async () => {
      try {
        const res = await fetch('/api/partner/handoff/peek', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticket: ticketRef.current }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not load this sign-in link.');
        setPeek(data);
        if (!data.newAccount) redeem(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [redeem]);

  const brand = (peek && PARTNERS[peek.partner]) || PARTNERS.limelite;
  const backHref = peek?.returnUrl || 'https://limelitecast.com';
  const asking = !!peek?.newAccount && !error;

  return (
    <div className="flex flex-col items-center justify-center text-center" style={{ minHeight: '60vh', padding: '48px 16px', gap: 20 }}>
      <div className="flex items-center" style={{ gap: 14 }}>
        <img src="/brand/wordmark-heyjoe.png" alt="Hey Joe" style={{ height: 40 }} className="dark:[filter:invert(96%)_sepia(8%)_saturate(280%)_hue-rotate(345deg)_brightness(102%)]" />
        {brand && (
          <>
            <span style={{ fontSize: 20, color: 'var(--hj-ink-3)' }}>×</span>
            <img src={brand.logo.replace('.svg', '-ink.svg')} alt={brand.name} style={{ height: 30 }} className="dark:hidden" />
            <img src={brand.logo} alt={brand.name} style={{ height: 30 }} className="hidden dark:block" />
          </>
        )}
      </div>

      {error ? (
        <>
          <p className="m-0" style={{ fontSize: 15, color: 'var(--hj-ink)', maxWidth: 420 }}>{error}</p>
          <Link to="/" style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink-2)' }}>Go to Hey Joe Plus</Link>
        </>
      ) : asking ? (
        <div
          className="w-full text-left"
          style={{ maxWidth: 440, padding: 24, borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-1)' }}
        >
          <h1 className="font-hj-display m-0" style={{ fontSize: 22, color: 'var(--hj-ink)', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            Continue to Hey Joe Plus{peek?.firstName ? `, ${peek.firstName}` : ''}
          </h1>
          <p className="m-0" style={{ marginTop: 12, fontSize: 14.5, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
            This creates a free Hey Joe account for <strong style={{ color: 'var(--hj-ink)' }}>{peek?.email}</strong>, using your name and email from {brand?.name || 'Limelite'}.
          </p>
          <p className="m-0" style={{ marginTop: 10, fontSize: 13, color: 'var(--hj-ink-3)', lineHeight: 1.5 }}>
            We'll email you about Hey Joe events. Unsubscribe anytime.
          </p>
          <p className="m-0" style={{ marginTop: 10, fontSize: 13, color: 'var(--hj-ink-3)', lineHeight: 1.5 }}>
            By continuing you agree to the{' '}
            <a href="https://heyjoe.io/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hj-ink-2)', textDecoration: 'underline' }}>Terms</a>
            {' '}and{' '}
            <a href="https://heyjoe.io/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hj-ink-2)', textDecoration: 'underline' }}>Privacy Policy</a>.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center" style={{ marginTop: 20, gap: 10 }}>
            <button
              type="button"
              onClick={() => redeem(true)}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ padding: '12px 22px', borderRadius: 999, background: 'var(--hj-yellow)', border: 0, color: 'var(--hj-beige-ink)', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Continue
            </button>
            <a
              href={backHref}
              className="inline-flex items-center justify-center"
              style={{ padding: '12px 18px', borderRadius: 999, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
            >
              Not you? Back to {brand?.name || 'Limelite'}
            </a>
          </div>
        </div>
      ) : (
        <p className="m-0 inline-flex items-center gap-2" style={{ fontSize: 15, color: 'var(--hj-ink-2)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
        </p>
      )}
    </div>
  );
};

export default PartnerHandoffPage;
